import api from './api';

/**
 * Enhanced Claude Code Service for comprehensive code development
 * Supports workspace management, real-time code generation, and mobile compatibility
 */
class ClaudeCodeService {
  constructor() {
    this.baseURL = '/api/projects';
    this.legacyBaseURL = '/claude-code'; // Keep legacy endpoints for backward compatibility
  }

  // ========== LEGACY METHODS (Backward Compatibility) ==========
  
  /**
   * Generate code using Claude Code CLI (Legacy)
   */
  async generateCode(params) {
    const response = await api.post(`${this.legacyBaseURL}/generate`, params);
    return response.data;
  }

  /**
   * Get code file content (Legacy)
   */
  async getCodeContent(filename) {
    const response = await api.get(`${this.legacyBaseURL}/${filename}`);
    return response.data;
  }

  /**
   * List code files for a project/conversation (Legacy)
   */
  async listCodeFiles(projectId, conversationId = null) {
    const url = conversationId 
      ? `${this.legacyBaseURL}/list/${projectId}/${conversationId}`
      : `${this.legacyBaseURL}/list/${projectId}`;
    
    const response = await api.get(url);
    return response.data;
  }

  /**
   * Delete a code file (Legacy)
   */
  async deleteCodeFile(filename) {
    const response = await api.delete(`${this.legacyBaseURL}/${filename}`);
    return response.data;
  }

  /**
   * Get direct URL to a code file for embedding (Legacy)
   */
  getCodeFileUrl(filename) {
    return `${api.defaults.baseURL}/uploads/code/${filename}`;
  }

  // ========== NEW ENHANCED METHODS ==========

  /**
   * Create a new workspace for a project
   */
  async createWorkspace(projectId, workspaceData = {}) {
    try {
      const response = await api.post(`${this.baseURL}/${projectId}/development/workspace/create`, {
        // Backend required fields (project_id comes from URL path)
        workspace_path: workspaceData.workspace_path || null,
        include_all_documents: workspaceData.include_all_documents !== undefined ? workspaceData.include_all_documents : true,
        generate_story_backlog: workspaceData.generate_story_backlog !== undefined ? workspaceData.generate_story_backlog : true,
        validation_mode: workspaceData.validation_mode || 'moderate'
      });
      return response.data;
    } catch (error) {
      console.error('Error creating workspace:', error);
      throw error;
    }
  }

  /**
   * Get workspace status and file listing
   */
  async getWorkspaceStatus(projectId) {
    try {
      const response = await api.get(`${this.baseURL}/${projectId}/development/workspace/status`);
      return response.data;
    } catch (error) {
      console.error('Error getting workspace status:', error);
      throw error;
    }
  }

  /**
   * Generate code from task description with real-time streaming
   */
  async generateCodeFromTask(projectId, taskData) {
    try {
      const response = await api.post(`${this.baseURL}/${projectId}/development/generate-code`, {
        task_description: taskData.task_description,
        task_type: taskData.task_type || 'implementation',
        language: taskData.language || 'python',
        framework: taskData.framework,
        additional_context: taskData.additional_context,
        stream_enabled: taskData.stream_enabled !== false
      });
      return response.data;
    } catch (error) {
      console.error('Error generating code:', error);
      throw error;
    }
  }

  /**
   * Read a specific file from the project workspace
   */
  async readFile(projectId, filePath) {
    try {
      const response = await api.get(`${this.baseURL}/projects/${projectId}/files/${encodeURIComponent(filePath)}`);
      return response.data;
    } catch (error) {
      console.error('Error reading file:', error);
      throw error;
    }
  }

  /**
   * Write content to a file in the project workspace
   */
  async writeFile(projectId, filePath, content) {
    try {
      const response = await api.put(`${this.baseURL}/projects/${projectId}/files/${encodeURIComponent(filePath)}`, {
        file_path: filePath,
        content: content
      });
      return response.data;
    } catch (error) {
      console.error('Error writing file:', error);
      throw error;
    }
  }

  /**
   * List files in the project workspace with optional filtering
   */
  async listFiles(projectId, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.directory) params.append('directory', options.directory);
      if (options.extension) params.append('extension', options.extension);

      const response = await api.get(`${this.baseURL}/projects/${projectId}/files?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error listing files:', error);
      throw error;
    }
  }

  /**
   * Delete a file from the project workspace
   */
  async deleteFile(projectId, filePath) {
    try {
      const response = await api.delete(`${this.baseURL}/projects/${projectId}/files/${encodeURIComponent(filePath)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Mobile-optimized workspace summary
   */
  async getMobileWorkspaceSummary(projectId) {
    try {
      const response = await api.get(`${this.baseURL}/mobile/projects/${projectId}/workspace/summary`);
      return response.data;
    } catch (error) {
      console.error('Error getting mobile workspace summary:', error);
      throw error;
    }
  }

  /**
   * Quick code generation for mobile apps
   */
  async mobileQuickGenerate(projectId, taskDescription, language = 'python') {
    try {
      const response = await api.post(`${this.baseURL}/mobile/projects/${projectId}/quick-generate`, {
        task_description: taskDescription,
        language: language
      });
      return response.data;
    } catch (error) {
      console.error('Error in mobile quick generate:', error);
      throw error;
    }
  }

  /**
   * Check if a project has an active workspace
   */
  async hasWorkspace(projectId) {
    try {
      const status = await this.getWorkspaceStatus(projectId);
      return status.workspace_exists || false;
    } catch (error) {
      return false;
    }
  }

  // ========== UTILITY METHODS ==========

  /**
   * Get supported languages and frameworks
   */
  getSupportedLanguages() {
    return [
      { value: 'python', label: 'Python', extensions: ['.py'] },
      { value: 'javascript', label: 'JavaScript', extensions: ['.js'] },
      { value: 'typescript', label: 'TypeScript', extensions: ['.ts'] },
      { value: 'html', label: 'HTML', extensions: ['.html'] },
      { value: 'css', label: 'CSS', extensions: ['.css'] },
      { value: 'json', label: 'JSON', extensions: ['.json'] },
      { value: 'markdown', label: 'Markdown', extensions: ['.md'] },
      { value: 'yaml', label: 'YAML', extensions: ['.yml', '.yaml'] }
    ];
  }

  getSupportedFrameworks() {
    return {
      python: ['FastAPI', 'Django', 'Flask', 'Streamlit', 'Pytest'],
      javascript: ['React', 'Vue', 'Angular', 'Node.js', 'Express', 'Jest'],
      typescript: ['React', 'Vue', 'Angular', 'Node.js', 'NestJS', 'Jest'],
      html: ['Bootstrap', 'Tailwind CSS', 'Material-UI'],
      css: ['Sass', 'Less', 'Styled Components'],
      json: [],
      markdown: [],
      yaml: []
    };
  }

  getTaskTypes() {
    return [
      { value: 'implementation', label: 'Implementation', description: 'Create new functionality' },
      { value: 'testing', label: 'Testing', description: 'Write tests for existing code' },
      { value: 'documentation', label: 'Documentation', description: 'Create documentation' },
      { value: 'refactoring', label: 'Refactoring', description: 'Improve existing code structure' },
      { value: 'debugging', label: 'Debugging', description: 'Fix bugs and issues' },
      { value: 'optimization', label: 'Optimization', description: 'Improve performance' }
    ];
  }

  /**
   * Get file language from extension
   */
  getLanguageFromExtension(fileName) {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const languageMap = {
      'py': 'python',
      'js': 'javascript',
      'ts': 'typescript',
      'tsx': 'typescript',
      'jsx': 'javascript',
      'html': 'html',
      'css': 'css',
      'scss': 'scss',
      'json': 'json',
      'md': 'markdown',
      'yml': 'yaml',
      'yaml': 'yaml',
      'txt': 'plaintext'
    };
    return languageMap[extension] || 'plaintext';
  }

  /**
   * Get file icon based on extension
   */
  getFileIcon(fileName) {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const iconMap = {
      'py': '🐍',
      'js': '📄',
      'ts': '📘',
      'tsx': '📘',
      'jsx': '📄',
      'html': '🌐',
      'css': '🎨',
      'scss': '🎨',
      'json': '⚙️',
      'md': '📝',
      'yml': '⚙️',
      'yaml': '⚙️',
      'txt': '📄'
    };
    return iconMap[extension] || '📄';
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Validate task data before submission
   */
  validateTaskData(taskData) {
    const errors = [];

    if (!taskData.task_description || taskData.task_description.trim().length < 10) {
      errors.push('Task description must be at least 10 characters long');
    }

    if (!taskData.language) {
      errors.push('Programming language is required');
    }

    const supportedLanguages = this.getSupportedLanguages().map(lang => lang.value);
    if (taskData.language && !supportedLanguages.includes(taskData.language)) {
      errors.push(`Unsupported language: ${taskData.language}`);
    }

    const supportedTaskTypes = this.getTaskTypes().map(type => type.value);
    if (taskData.task_type && !supportedTaskTypes.includes(taskData.task_type)) {
      errors.push(`Unsupported task type: ${taskData.task_type}`);
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }
}

// Create and export singleton instance
const claudeCodeService = new ClaudeCodeService();
export { claudeCodeService };
export default claudeCodeService;